import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import Key from '../clientVariables.json';

const ContactContext = createContext();

export const ContactProvider = ({ children }) => {
  // Define our contacts structure with all fields that match the database
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Custom setFilteredContacts function that also updates pagination totalCount
  const updateFilteredContacts = (newFilteredContacts) => {
    setFilteredContacts(newFilteredContacts);
    setPagination(prev => ({
      ...prev,
      totalCount: newFilteredContacts.length
    }));
  };
  const [selectedJob, setSelectedJob] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState({
    subject: '',
    body: 'Hi [Name],\n\nWe thought this job might interest you:\n\n[Job Description]\n\nLet us know if you\'re interested.'
  });
    // Add pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    totalCount: 0
  });
  
  // State to track all available tags with some predefined ones
  const [availableTags, setAvailableTags] = useState([
    'Tech', 'Finance', 'Healthcare', 'Marketing', 'Sales',
    'Executive', 'Engineering', 'Design', 'Product', 'HR',
    'Creative', 'Leadership'
  ]);

  const toggleContactSelection = (id) => {
    const updatedContacts = contacts.map(contact => {
      if (contact.id === id) {
        return { ...contact, selected: !contact.selected };
      }
      return contact;
    });
    setContacts(updatedContacts);
    setFilteredContacts(
      filteredContacts.map(contact => {
        if (contact.id === id) {
          return { ...contact, selected: !contact.selected };
        }
        return contact;
      })
    );
  };
  const selectAllContacts = (selected) => {
    // Get the contacts on the current page
    const start = pagination.page * pagination.rowsPerPage;
    const end = start + pagination.rowsPerPage;
    const paginatedContacts = filteredContacts.slice(start, end);
    const paginatedContactIds = paginatedContacts.map(contact => contact.id);
    
    // Update only the contacts that are on the current page
    const updatedContacts = contacts.map(contact => {
      if (paginatedContactIds.includes(contact.id)) {
        return { ...contact, selected };
      }
      return contact;
    });
    
    setContacts(updatedContacts);
    
    // Update filtered contacts
    const updatedFilteredContacts = filteredContacts.map(contact => {
      if (paginatedContactIds.includes(contact.id)) {
        return { ...contact, selected };
      }
      return contact;
    });
    
    setFilteredContacts(updatedFilteredContacts);
  };const addContact = async (contact) => {
    try {
      // Add any new tags to the available tags list
      if (contact.tags && contact.tags.length > 0) {
        addNewTags(contact.tags);
      }
      
      // Instead of manually adding to state, fetch fresh data from API
      await fetchContacts();
      
      // Return the contact for any chained operations
      return contact;
    } catch (error) {
      console.error("Error refreshing contacts after add:", error);
      // Fall back to manual addition if fetch fails
      const newContact = { ...contact, id: contact.id || uuidv4(), selected: false };
      setContacts(prev => [...prev, newContact]);
      setFilteredContacts(prev => [...prev, newContact]);
      return newContact;
    }
  };
  // Function to add new tags to the available tags list
  const addNewTags = (newTags) => {
    if (!newTags || newTags.length === 0) return;
    
    setAvailableTags(prevTags => {
      const uniqueNewTags = newTags.filter(tag => !prevTags.includes(tag));
      if (uniqueNewTags.length === 0) return prevTags;
      return [...prevTags, ...uniqueNewTags];
    });
  };
  const addContacts = async (contactsArray) => {
    // Add multiple contacts at once (more efficient for CSV imports)
    if (!contactsArray || contactsArray.length === 0) return 0;
    
    try {
      // Collect all tags from the new contacts to add to available tags
      const newTagsFromContacts = Array.from(
        new Set(contactsArray.flatMap(contact => contact.tags || []))
      );
      addNewTags(newTagsFromContacts);
      
      // Here you would typically make an API call to bulk add contacts
      // For now, we'll just call the addContact function for each contact
      // This is inefficient but works until a bulk API is available
      
      // In a real application, you would create a bulk API endpoint:
      // await axios.post(`${Key.domain_jnp}/common/bulk-add-contacts`, { contacts: contactsArray });      // Use Promise.all with map instead of a for-of loop with await
      await Promise.all(contactsArray.map(contact => {
        // You should implement a proper bulk add API instead of this
        return axios.post(`${Key.domain}/common/add-contactCRM`, {
          name: contact.name,
          email: contact.email,
          phone_number: contact.phone || '', // Correct field name for phone number
          job_title: contact.jobTitle || '', // Correct field name for job title
          city_name: contact.location?.city || '',
          state_name: contact.location?.state || '',
          city_id: contact.location?.cityId || null,
          state_id: contact.location?.stateId || null,
          linkedin_url: contact.linkedinUrl || '', // Added LinkedIn URL
          tags: contact.tags?.join(',') || '',
          admin_id: localStorage.getItem('admin_id') || sessionStorage.getItem('admin_id')
        });
      }));
      
      // After all contacts are added, refresh the list
      await fetchContacts();
      
      return contactsArray.length; // Return the number of added contacts
    } catch (error) {
      console.error("Error bulk adding contacts:", error);
      return 0;
    }
  };

  const selectedContacts = contacts.filter(contact => contact.selected);

  const sendJob = async () => {
    // Simulate sending job emails
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(selectedContacts.length);
      }, 1000);
    });
  };  // Handle page change for server-side pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
    
    // Call fetchContacts with the new page number for server-side pagination
    fetchContacts(false);
  };

  // Handle rows per page change for server-side pagination
  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setPagination(prev => ({
      ...prev,
      page: 0, // Reset to first page
      rowsPerPage: newRowsPerPage
    }));
    
    // Call fetchContacts with the new rows per page for server-side pagination
    fetchContacts(true);
  };

  const deleteContacts = async (contactIds) => {
    if (!contactIds || contactIds.length === 0) return false;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get admin ID from localStorage or sessionStorage
      const adminId = localStorage.getItem('admin_id') || sessionStorage.getItem('admin_id');
      
      // Delete contacts via API
      const response = await axios.post(`${Key.domain}/common/delete-contactCRM`, {
        admin_id: adminId,
        contact_ids: contactIds
      });
        if (response.data && (response.data.status === true || response.data.success === 1)) {
        // Update the local state by removing the deleted contacts
        setContacts(prevContacts => 
          prevContacts.filter(contact => !contactIds.includes(contact.id))
        );
        
        setFilteredContacts(prevFilteredContacts => 
          prevFilteredContacts.filter(contact => !contactIds.includes(contact.id))
        );
        
        // Update pagination total count
        setPagination(prev => ({
          ...prev,
          totalCount: prev.totalCount - contactIds.length
        }));
        
        setIsLoading(false);
        return true;
      }
      
      setError('Failed to delete contacts');
      setIsLoading(false);
      return false;
    } catch (err) {
      console.error('Error deleting contacts:', err);
      setError('Failed to delete contacts. Please try again later.');
      setIsLoading(false);
      return false;
    }
  };

  // Get paginated contacts - no need to slice since data is already paginated from server
  const getPaginatedContacts = () => {
    return filteredContacts;
  };  // Current search filters state
  const [searchFilters, setSearchFilters] = useState({
    jobTitle: '', // matches job_title in API
    state: '', // matches state_id or state_name in API
    city: '', // matches city_id or city_name in API
    searchQuery: '', // for general search across name, email, phone
    fromDate: null, // for filtering by created_at date range
    toDate: null // for filtering by created_at date range
  });  // Set search filters and trigger a search
  const setSearch = (filters) => {
    // Check if filters is an empty object (from clear button)
    const isEmpty = Object.keys(filters).length === 0;
    
    // Ensure filters object has all required fields
    const updatedFilters = isEmpty 
      ? { jobTitle: '', state: '', city: '', searchQuery: '', fromDate: null, toDate: null } 
      : {
          jobTitle: filters.jobTitle || '',
          state: filters.state || '',
          city: filters.city || '',
          searchQuery: filters.searchQuery || '',
          fromDate: filters.fromDate || null,
          toDate: filters.toDate || null,
          ...filters // Keep any other filters that might be passed
        };
    
    console.log("Setting search filters:", updatedFilters);
    setSearchFilters(updatedFilters);
    fetchContacts(true); // Reset pagination and search
  };
  // Fetch contacts from the API with server-side pagination and search
  const fetchContacts = async (resetPagination = true, customFilters = null) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get admin ID from localStorage or sessionStorage if needed
      const adminId = localStorage.getItem('admin_id') || sessionStorage.getItem('admin_id');
      
      // If resetPagination is true, reset to page 0
      if (resetPagination) {
        setPagination(prev => ({
          ...prev,
          page: 0
        }));
      }
      
      // Use custom filters if provided, otherwise use the current search filters
      const filters = customFilters || searchFilters;
      
      console.log("Fetching contacts with filters:", filters);
        // Add parameters for server-side pagination and search
      const response = await axios.post(`${Key.domain}/common/get-contactCRM`, {
        admin_id: adminId,
        page: resetPagination ? 0 : pagination.page,
        page_size: pagination.rowsPerPage,
        job_title: filters.jobTitle || '',
        // Allow for both name and ID for state/city
        state_id: typeof filters.state === 'number' ? filters.state : null,
        state_name: typeof filters.state === 'string' ? filters.state : '',
        city_id: typeof filters.city === 'number' ? filters.city : null,
        city_name: typeof filters.city === 'string' ? filters.city : '',
        search_query: filters.searchQuery || '',
        // Add date filters
        from_date: filters.fromDate ? filters.fromDate.toISOString().split('T')[0] : '',
        to_date: filters.toDate ? filters.toDate.toISOString().split('T')[0] : ''
      });console.log("API Response:", response.data);
      
      // Check for different possible response formats
      if (response.data) {
        let fetchedContacts = [];
        let paginationData = {};
        
        // Handle different response structures
        if (response.data.status === true) {
          // New format with direct contacts array
          fetchedContacts = response.data.contacts || [];
          paginationData = response.data.pagination || {};
        } else if (response.data.success === 1 && response.data.data) {
          // Legacy format with nested data object
          if (Array.isArray(response.data.data)) {
            fetchedContacts = response.data.data;
          } else if (response.data.data.contacts) {
            fetchedContacts = response.data.data.contacts;
            paginationData = response.data.data.pagination || {};
          }
        } else if (Array.isArray(response.data)) {
          // Direct array format
          fetchedContacts = response.data;
        }
        
        // Ensure we have default pagination data if not provided
        if (!paginationData.total) {
          paginationData = {
            total: fetchedContacts.length,
            page: pagination.page,
            page_size: pagination.rowsPerPage,
            total_pages: Math.ceil(fetchedContacts.length / pagination.rowsPerPage),
            ...paginationData
          };
        }
          // Map the API response to match our contact structure
        const formattedContacts = fetchedContacts.map(contact => ({
          id: contact.id.toString(),
          name: contact.name,
          email: contact.email,
          phone: contact.phone_number || '', // Using phone_number field from API
          jobTitle: contact.job_title || '', // Using job_title field from API
          linkedinUrl: contact.linkedin_url || '', // Adding LinkedIn URL from API
          location: {
            state: contact.state_name || '',
            city: contact.city_name || '',
            stateId: contact.state_id || null,
            cityId: contact.city_id || null,
            country: 'United States',
          },
          tags: contact.tags ? contact.tags.split(',').map(tag => tag.trim()) : [],
          createdAt: new Date(contact.created_at), // Adding created date from API
          updatedAt: new Date(contact.updated_at), // Adding updated date from API
          selected: false
        }));
        
        setContacts(formattedContacts);
        setFilteredContacts(formattedContacts);
        
        // Update pagination with server values
        setPagination(prev => ({
          ...prev,
          page: paginationData.page,
          totalCount: paginationData.total
        }));
        
        // Extract tags from contacts
        const newTagsFromContacts = Array.from(
          new Set(formattedContacts.flatMap(contact => contact.tags || []))
        );
        addNewTags(newTagsFromContacts);      } else {
        // If no contacts or error, set to empty array
        setContacts([]);
        setFilteredContacts([]);
        setPagination(prev => ({
          ...prev,
          totalCount: 0
        }));
          
        // Handle different error response formats
        if (response.data) {
          if (response.data.status === false) {
            setError(response.data.message || 'Failed to fetch contacts');
          } else if (response.data.success === 0) {
            setError(response.data.error || 'Failed to fetch contacts');
          } else {
            setError('No contacts found or invalid response format');
          }
        } else {
          setError('Failed to fetch contacts: Empty response');
        }
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts. Please try again later.');
      
      // Set empty arrays when there's an error
      setContacts([]);
      setFilteredContacts([]);
      setPagination(prev => ({
        ...prev,
        totalCount: 0
      }));
    } finally {
      setIsLoading(false);
    }
  };
    // Load contacts on component mount with a refresh function
  useEffect(() => {
    fetchContacts();
    
    // Optional: Set up a refresh interval if needed
    // const refreshInterval = setInterval(() => {
    //   fetchContacts(false); // Don't reset pagination when auto-refreshing
    // }, 60000); // refresh every minute
    
    // return () => clearInterval(refreshInterval);
  }, []);
  // When search filters change, update the search and fetch new data
  useEffect(() => {
    // Always fetch contacts when filters change, even if they're empty
    // This ensures that clearing filters properly resets the list
    fetchContacts(true);
  }, [searchFilters]);return (
    <ContactContext.Provider
      value={{
        contacts,
        filteredContacts,
        setFilteredContacts: updateFilteredContacts,
        selectedContacts,
        toggleContactSelection,
        selectAllContacts,
        addContact,
        addContacts,
        selectedJob,
        setSelectedJob,
        emailTemplate,
        setEmailTemplate,
        sendJob,
        availableTags,
        addNewTags,
        pagination,
        handlePageChange,
        handleChangeRowsPerPage,
        getPaginatedContacts,
        isLoading,
        error,
        fetchContacts,
        // Expose search functionality
        searchFilters,
        setSearch,
        // Contact deletion
        deleteContacts
      }}
    >
      {children}
    </ContactContext.Provider>
  );
  // Function to delete selected contacts
  
};

export const useContactContext = () => {
  const context = useContext(ContactContext);
  if (context === undefined) {
    throw new Error('useContactContext must be used within a ContactProvider');
  }
  return context;
};
